import { z } from "zod";
/**
 * Per-project custom fields.
 *
 * A project defines fields (text, number, dropdown, date, person); every task
 * in it may carry a value for each. Declared once so the server validates what
 * the web sends and both agree on how a value is spelled:
 *   text -> string, number -> number, dropdown -> option id, date -> "YYYY-MM-DD",
 *   person -> user id. `null` clears the value.
 */
export const CUSTOM_FIELD_TYPES = ["text", "number", "dropdown", "date", "person"];
export const MAX_CUSTOM_FIELDS = 30;
export const MAX_DROPDOWN_OPTIONS = 50;
export const MAX_TEXT_VALUE = 2000;
export const CUSTOM_FIELD_COLORS = ["gray", "red", "orange", "yellow", "green", "teal", "blue", "purple", "pink"];
export const customFieldOption = z
    .object({
    id: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/),
    label: z.string().trim().min(1).max(60),
    color: z.enum(CUSTOM_FIELD_COLORS).default("gray"),
})
    .strict();
const fieldName = z.string().trim().min(1).max(60);
const optionList = z
    .array(customFieldOption)
    .max(MAX_DROPDOWN_OPTIONS)
    .refine((list) => new Set(list.map((o) => o.id)).size === list.length, "Option ids must be unique.")
    .refine((list) => new Set(list.map((o) => o.label.toLowerCase())).size === list.length, "Option labels must be unique.");
export const createCustomFieldBody = z
    .object({
    name: fieldName,
    type: z.enum(CUSTOM_FIELD_TYPES),
    options: optionList.optional(),
})
    .strict();
/** The type is fixed once created: changing it would orphan every stored value. */
export const patchCustomFieldBody = z
    .object({
    name: fieldName.optional(),
    options: optionList.optional(),
})
    .strict();
export const reorderCustomFieldsBody = z
    .object({ fieldIds: z.array(z.string().min(1).max(64)).min(1).max(MAX_CUSTOM_FIELDS) })
    .strict();
export const setCustomValueBody = z.object({ value: z.union([z.string(), z.number(), z.null()]) }).strict();
/* ─────────────────────────────── value rules ─────────────────────────────── */
const DATE = /^\d{4}-\d{2}-\d{2}$/;
export function isCalendarDate(value) {
    if (!DATE.test(value))
        return false;
    const d = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
/**
 * Validates and normalises a value for a field. Person membership in the
 * office is the server's check; here a person only has to look like an id.
 */
export function checkCustomValue(field, raw) {
    if (raw === null || raw === undefined || raw === "")
        return { ok: true, value: null };
    switch (field.type) {
        case "text": {
            if (typeof raw !== "string")
                return { ok: false, error: "Enter text." };
            const text = raw.trim();
            if (text.length > MAX_TEXT_VALUE)
                return { ok: false, error: `Text can be at most ${MAX_TEXT_VALUE} characters.` };
            return { ok: true, value: text === "" ? null : text };
        }
        case "number": {
            const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : NaN;
            if (!Number.isFinite(n) || Math.abs(n) > 1e15)
                return { ok: false, error: "Enter a number." };
            return { ok: true, value: n };
        }
        case "dropdown": {
            if (typeof raw !== "string" || !field.options.some((o) => o.id === raw))
                return { ok: false, error: "Choose one of the options." };
            return { ok: true, value: raw };
        }
        case "date": {
            if (typeof raw !== "string" || !isCalendarDate(raw))
                return { ok: false, error: "Enter a date as YYYY-MM-DD." };
            return { ok: true, value: raw };
        }
        case "person": {
            if (typeof raw !== "string" || !/^[A-Za-z0-9_-]{8,64}$/.test(raw))
                return { ok: false, error: "Choose a person." };
            return { ok: true, value: raw };
        }
    }
}
/* ─────────────────────────────── filters ─────────────────────────────── */
/** equals/contains for text, gte/lte for number and date, in for dropdown and person. */
export const CUSTOM_FILTER_OPS = ["eq", "contains", "gte", "lte", "in"];
export const customFilter = z
    .object({
    fieldId: z.string().min(1).max(64),
    op: z.enum(CUSTOM_FILTER_OPS),
    /** `in` takes a comma-separated list of ids. */
    value: z.string().max(400),
})
    .strict();
export const MAX_CUSTOM_FILTERS = 10;
/** Which operators make sense for which field type. */
export const OPS_FOR_TYPE = {
    text: ["eq", "contains"],
    number: ["eq", "gte", "lte"],
    dropdown: ["in"],
    date: ["eq", "gte", "lte"],
    person: ["in"],
};
/** A task's value against one filter: the single definition the web uses on loaded tasks. */
export function matchesCustomFilter(field, filter, value) {
    if (!OPS_FOR_TYPE[field.type].includes(filter.op))
        return false;
    if (value === undefined)
        return false;
    switch (filter.op) {
        case "eq":
            return field.type === "number" ? Number(filter.value) === value : String(value).toLowerCase() === filter.value.toLowerCase();
        case "contains":
            return String(value).toLowerCase().includes(filter.value.toLowerCase());
        case "gte":
        case "lte": {
            if (field.type === "number") {
                const bound = Number(filter.value);
                if (!Number.isFinite(bound))
                    return false;
                return filter.op === "gte" ? Number(value) >= bound : Number(value) <= bound;
            }
            return filter.op === "gte" ? String(value) >= filter.value : String(value) <= filter.value;
        }
        case "in":
            return filter.value.split(",").includes(String(value));
    }
}
/** The table column id for a field. */
export const customColumnId = (fieldId) => `cf:${fieldId}`;
export const CUSTOM_COLUMN = /^cf:[A-Za-z0-9_-]{1,64}$/;
//# sourceMappingURL=custom-fields.js.map